import * as React from 'react';
import { LanguageContext } from '../utilities/LocalizationModule';
import TopNavigator from "../components/TopNavigator"
import { DefaultButton, Stack } from "@fluentui/react"
import UsersTable from '../components/displays/UsersTable';
import { Routes, Route, Navigate } from "react-router-dom";
import { connect, ConnectedProps } from 'react-redux';
import { IPortalUser } from '../data-structures/interfaces';
import { getCurrentProfile } from '../utilities/helpers/ApiHelper';
import { PortalSignInOutcome, retryPortalSignIn, signInToPortal } from '../utilities/helpers/portalSignIn';
import { setUserProfile } from "../redux/modules/user"
import { getRequests } from "../redux/modules/requests"
import { getDatasourceTypes } from "../redux/modules/datasource"
import Dashboard from '../components/displays/Dashboard';
import Subscriptions from '../components/displays/Subsriptions';
import MessageScreen from "./MessageScreen"
import NavMenu from "../components/NavMenu"
import { MainViewStyle } from "../styles/HomeStyles"
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import Loading from '../components/Loading';
import { RootState } from '../redux';
import PartnersOverview from "../components/displays/PartnersOverview";
import Requests from "../components/displays/Requests";
import CopilotPrompts from "../components/displays/CopilotPrompts";
import { AuthHelper } from '../utilities/helpers/AuthHelper';

const mapDispatchToProps = {
    setUserProfile,
    getRequests,
    getDatasourceTypes
};

const mapStateToProps = (state: RootState) => {
    return {
        requests: state.requests.requests,
        isGlobalAdmin: state.user.isGlobalAdmin
    }
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
interface HomeProps extends PropsFromRedux { }

const Home: React.FC<HomeProps> = ({
    setUserProfile,
    requests,
    getRequests,
    getDatasourceTypes,
    isGlobalAdmin
}: HomeProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isUnauthorized, setIsUnauthorized] = React.useState<boolean>(false);
    const [signInFailed, setSignInFailed] = React.useState<boolean>(false);
    const [userTenantName, setUserTenantName] = React.useState<string>("");

    const { instance, accounts, inProgress } = useMsal();
    // Once only: MSAL is busy, then idle again, whenever a popup asks for a token.
    const signInStarted = React.useRef<boolean>(false);

    React.useEffect(() => {
        // App.tsx shows Home as soon as an account is cached, before MSAL has saved the tokens a
        // return from Microsoft brought back: signing in then used the old cache and showed Try
        // again (2026-10-21).
        if (inProgress !== InteractionStatus.None || signInStarted.current) {
            return;
        }
        signInStarted.current = true;
        updateData();
    }, [inProgress]);

    const updateData = async () => {
        setIsLoading(true);
        try {
            AuthHelper.setSignOutCallback(signOut);
            // The Entra ID token proves who signs in (spec §1.2); no identity goes in a body.
            const signIn: PortalSignInOutcome = await signInToPortal(instance, accounts[0]);
            if (signIn === "redirect") {
                // On its way through login.microsoftonline.com; the Portal loads again after it.
                return;
            }

            if (signIn === "ok") {
                let userProfile: IPortalUser = await getCurrentProfile();
                if (userProfile) {
                    let userTenantName: string;
                    userProfile.tenants = userProfile.tenants.map(tenant => {

                        if (!userTenantName && tenant.tenantId === userProfile.tenantId && tenant.friendlyName) {
                            userTenantName = tenant.friendlyName.charAt(0).toUpperCase() + tenant.friendlyName.slice(1);
                            setUserTenantName(userTenantName);
                        }

                        const advancedFields = (tenant.allowedAdvancedSearchFields as string)?.trim().toLowerCase().split(";") ?? [];
                        const allowedTeams = (tenant.allowedTeams as string)?.trim().toLowerCase().split(";") ?? [];
                        const allowedSiteCollections = (tenant.allowedSiteCollections as string)?.trim().toLowerCase().split(";") ?? [];
                        const hiddenMetadataFields = (tenant.hiddenMetadataFields as string)?.trim().toLowerCase().split(";") ?? [];
                        return ({
                            ...tenant,
                            allowedAdvancedSearchFields: advancedFields,
                            allowedSiteCollections,
                            allowedTeams,
                            hiddenMetadataFields,
                        });
                    });
                    setUserProfile(userProfile, accounts[0]);
                    if (requests.length === 0 && userProfile.isGlobalAdmin) getRequests();
                    getDatasourceTypes();
                }
                else {
                    setIsUnauthorized(true);
                }
            }
            else if (signIn === "refused") {
                // The API refused this account: offer another one (the screen's button signs out),
                // without ending the user's Microsoft session on its own.
                setIsUnauthorized(true);
            }
            else {
                // Throttled, the API or Microsoft unreachable, or no token without the user: retry.
                setSignInFailed(true);
            }
        } catch (e) {
            console.error(e);
        }

        setIsLoading(false);
    };

    const signOut = () => {
        instance.logoutRedirect();
    }

    if (signInFailed) {
        // The sign-in can be retried; the same screen as App.tsx's AuthError.
        return <MessageScreen
            text={languageStrings.AuthFailed}
            actionButton={
                <DefaultButton onClick={() => retryPortalSignIn()}>
                    {languageStrings.TryAgain}
                </DefaultButton>
            }
        />;
    }

    return isLoading
        ? <Loading label={languageStrings.LoadingPortal} />
        : !isUnauthorized
            ? <React.Fragment>
                <TopNavigator userTenantName={userTenantName} signOut={signOut} />
                <Stack horizontal>
                    <Stack.Item>
                        <NavMenu />
                    </Stack.Item>
                    <Stack.Item className={MainViewStyle}>
                        <Routes>
                            <Route index element={<Navigate to="/dashboard" replace />} />
                            <Route path="dashboard" element={<Dashboard updateData={updateData} />} />
                            <Route path="users" element={<UsersTable />} />
                            {/* Partners views are admin-only: register the routes only for
                                global admins so non-admins can't reach them by direct URL.
                                Non-admins fall through to the catch-all -> /dashboard. */}
                            {isGlobalAdmin && <Route path="partners/overview" element={<PartnersOverview />} />}
                            {isGlobalAdmin && <Route path="partners/requests" element={<Requests />} />}
                            {isGlobalAdmin && <Route path="copilot-prompts" element={<CopilotPrompts />} />}
                            {/* Single optional-param route: /subscriptions and /subscriptions/:id
                                share ONE <Subscriptions/> instance, so toggling between them
                                re-scopes the live SignalR connection instead of remounting it. */}
                            <Route path="subscriptions/:tenantId?" element={<Subscriptions />} />
                            <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                    </Stack.Item>
                </Stack>
            </React.Fragment>
            : <MessageScreen
                text={languageStrings.UnauthorizedMessage}
                actionButton={
                    <DefaultButton onClick={signOut}>
                        {languageStrings.SignInDifferentAccount}
                    </DefaultButton>
                }
            />
}

export default connector(Home);